import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

import type { ChecklistStatus } from '../shared/checklistUtils';
import { getStatusLabel } from '../shared/format';
import type { ChecklistItemMetadata, ManualOverrideDecision } from '../types';

export function toChecklistStatus(status: CmChecklist.Status): ChecklistStatus {
  return status === 'requires_approval' ? 'requires approval' : status;
}

export interface PresentedRowBase extends ChecklistItemMetadata {
  id: string;
  status: ChecklistStatus;
  statusLabel: string;
}

// Presented `status` is the automatic result; manual overrides stay a separate state layer.
export function presentRowBase(
  row: Pick<CmChecklist.Row, 'id' | 'item_key' | 'evidence_hash' | 'status' | 'manual_override'>,
): PresentedRowBase {
  const status = toChecklistStatus(row.status);

  return {
    evidenceHash: row.evidence_hash,
    id: row.id,
    itemKey: row.item_key,
    manualOverride: toManualOverrideDecision(row.manual_override),
    status,
    statusLabel: getStatusLabel(status),
  };
}

function toManualOverrideDecision(
  manualOverride: CmChecklist.Row['manual_override'],
): ManualOverrideDecision | undefined {
  if (!manualOverride?.matches_current_evidence) {
    return undefined;
  }

  return manualOverride.is_approved ? 'approved' : 'requires_approval';
}

// svc-offer can ship a rule before this client has its text; drop that row instead of crashing the page.
export function warnMissingRuleText(row: { item_key: string; rule_id: string }) {
  console.warn(`CM checklist: dropping row with no rule text for "${row.rule_id}" (${row.item_key})`);
}

export function formatEvidenceValue(value?: string | number | boolean | null) {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  return String(value);
}
